/**
 * Local AI usage log. Black Cherry's AI calls (chat / filler / virality) run in the
 * browser with the user's own key, so usage was never recorded anywhere. This logs each
 * call to localStorage — tokens, estimated cost, provider/model, and which video — so the
 * user can see per-video and lifetime totals. Stored only in this browser.
 */

export interface AiUsageRecord {
  ts: number;
  transcriptId: number | null;
  videoTitle: string;
  feature: string; // 'chat' | 'filler' | 'virality'
  provider: string;
  model: string;
  tokensIn: number;
  tokensOut: number;
  costUSD: number;
}

const KEY = "cc_ai_usage_log";
const MAX_RECORDS = 5000;

export function logAiUsage(rec: AiUsageRecord): void {
  try {
    const arr: AiUsageRecord[] = JSON.parse(localStorage.getItem(KEY) || "[]");
    arr.push(rec);
    // Keep the log bounded so it can't grow without limit.
    if (arr.length > MAX_RECORDS) arr.splice(0, arr.length - MAX_RECORDS);
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch (e) {
    /* localStorage unavailable / full — usage logging is best-effort */
  }
}

export function getAiUsage(): AiUsageRecord[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch (e) {
    return [];
  }
}

export function clearAiUsage(): void {
  try {
    localStorage.removeItem(KEY);
  } catch (e) {
    /* ignore */
  }
}

export interface UsageTotals {
  calls: number;
  tokensIn: number;
  tokensOut: number;
  costUSD: number;
}

export function totals(records: AiUsageRecord[]): UsageTotals {
  return records.reduce(
    (a, r) => ({
      calls: a.calls + 1,
      tokensIn: a.tokensIn + (r.tokensIn || 0),
      tokensOut: a.tokensOut + (r.tokensOut || 0),
      costUSD: a.costUSD + (r.costUSD || 0),
    }),
    { calls: 0, tokensIn: 0, tokensOut: 0, costUSD: 0 },
  );
}

export interface VideoUsage extends UsageTotals {
  transcriptId: number | null;
  videoTitle: string;
}

export function byVideo(records: AiUsageRecord[]): VideoUsage[] {
  const map = new Map<string, VideoUsage>();
  for (const r of records) {
    const k = String(r.transcriptId ?? "unknown");
    const cur =
      map.get(k) || {
        transcriptId: r.transcriptId,
        videoTitle: r.videoTitle || "Untitled",
        calls: 0,
        tokensIn: 0,
        tokensOut: 0,
        costUSD: 0,
      };
    cur.calls += 1;
    cur.tokensIn += r.tokensIn || 0;
    cur.tokensOut += r.tokensOut || 0;
    cur.costUSD += r.costUSD || 0;
    if (r.videoTitle) cur.videoTitle = r.videoTitle;
    map.set(k, cur);
  }
  return Array.from(map.values()).sort((a, b) => b.costUSD - a.costUSD);
}
